// Small DOM + formatting helpers and the toast system.
import { explorerTx } from "./config.js";
import { describeError } from "./errors.js";

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

/** Only allow http(s) and our own relative URLs in href/src. */
export function safeUrl(u) {
  if (!u) return "";
  try {
    const url = new URL(u, location.origin);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : "";
  } catch {
    return "";
  }
}

export const short = (pk, n = 4) => {
  const s = pk?.toString?.() ?? String(pk ?? "");
  return s.length > n * 2 + 1 ? `${s.slice(0, n)}…${s.slice(-n)}` : s;
};

/** lamports (bigint) -> "1.2345" */
export function fmtSol(lamports, digits = 4) {
  const v = Number(lamports) / 1e9;
  return fmtNum(v, digits);
}
export function fmtNum(v, digits = 2) {
  if (!isFinite(v)) return "-";
  if (v === 0) return "0";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (a >= 1e4) return (v / 1e3).toFixed(1) + "K";
  if (a >= 1) return v.toLocaleString("en-US", { maximumFractionDigits: digits });
  if (a >= 0.0001) return v.toFixed(Math.max(digits, 4)).replace(/0+$/, "").replace(/\.$/, "");
  if (a >= 1e-9) return v.toFixed(9).replace(/0+$/, "");
  return v.toExponential(2);
}
/** token base units (bigint) -> human */
export function fmtTokens(units, digits = 2) {
  return fmtNum(Number(units) / 1e6, digits);
}
/** tiny prices like 0.000000028 shown with 3 significant digits */
export function fmtPrice(sol) {
  if (!sol || !isFinite(sol)) return "0";
  if (sol >= 0.0001) return fmtNum(sol, 6);
  const zeros = Math.floor(-Math.log10(sol));
  if (zeros > 12) return sol.toExponential(2);
  return sol.toFixed(zeros + 2).replace(/0+$/, "");
}export const fmtPct = (f, d = 1) => `${(f * 100).toFixed(d)}%`;
export const fmtBps = (bps) => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;

export function fmtAge(sec) {
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** Parse a decimal string to base units (bigint) without float errors. */
export function parseUnits(str, decimals) {
  const s = String(str ?? "").trim().replace(/,/g, "");
  if (!/^\d*\.?\d*$/.test(s) || s === "" || s === ".") return null;
  const [i, f = ""] = s.split(".");
  const frac = (f + "0".repeat(decimals)).slice(0, decimals);
  return BigInt(i || "0") * 10n ** BigInt(decimals) + BigInt(frac || "0");
}
export function formatUnits(v, decimals) {
  const neg = v < 0n;
  let x = neg ? -v : v;
  const base = 10n ** BigInt(decimals);
  const i = x / base;
  const f = (x % base).toString().padStart(decimals, "0").replace(/0+$/, "");
  return (neg ? "-" : "") + i.toString() + (f ? "." + f : "");
}

// ------------------------------------------------------------------ toasts
let host;
export function toast({ kind = "info", title, body = "", signature, timeout } = {}) {
  if (!host) {
    host = document.createElement("div");
    host.className = "toasts";
    host.setAttribute("role", "status");
    host.setAttribute("aria-live", "polite");
    document.body.appendChild(host);
  }
  const el = document.createElement("div");
  el.className = `toast toast-${kind}`;
  el.innerHTML = `
    <div class="toast-dot"></div>
    <div class="toast-text">
      <strong>${esc(title)}</strong>
      ${body ? `<span>${esc(body)}</span>` : ""}
      ${signature ? `<a href="${esc(explorerTx(signature))}" target="_blank" rel="noopener">View on Solana Explorer ↗</a>` : ""}
    </div>
    <button class="toast-x" aria-label="Dismiss">×</button>`;
  host.appendChild(el);
  const close = () => {
    el.classList.add("out");
    setTimeout(() => el.remove(), 250);
  };
  el.querySelector(".toast-x").onclick = close;
  const t = timeout ?? (kind === "error" ? 12000 : 7000);
  if (t > 0) setTimeout(close, t);
  return { close, el };
}

/** Run an action with a button busy state and toasts. */
export async function runTx(button, label, fn) {
  const original = button?.innerHTML;
  const setBtn = (txt) => {
    if (button) button.innerHTML = `<span class="spin"></span>${esc(txt)}`;
  };
  if (button) button.disabled = true;
  let pending;
  try {
    const res = await fn((stage, info) => {
      const map = {
        preparing: "Preparing…",
        simulating: "Checking…",
        signing: "Approve in Phantom…",
        sending: "Sending…",
        confirming: "Confirming…",
        confirmed: "Done",
      };
      setBtn(map[stage] || "Working…");
      if (stage === "confirming" && info?.signature) {
        pending = toast({ kind: "info", title: `${label}: waiting for confirmation`, signature: info.signature, timeout: 0 });
      }
    });
    pending?.close();
    toast({ kind: "success", title: `${label} confirmed`, signature: res?.signature });
    return res;
  } catch (e) {
    pending?.close();
    console.error(e);
    toast({ kind: "error", title: `${label} failed`, body: describeError(e), signature: e.signature });
    return null;
  } finally {
    if (button) {
      button.disabled = false;
      button.innerHTML = original;
    }
  }
}

export function placeholderArt(seed, symbol = "") {
  // deterministic gradient avatar for tokens without an image
  let h = 0;
  for (const c of String(seed)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = 170 + (h % 50);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><defs><radialGradient id='g' cx='35%' cy='30%'><stop offset='0' stop-color='hsl(${hue},100%,70%)'/><stop offset='1' stop-color='hsl(${hue + 20},80%,14%)'/></radialGradient></defs><rect width='100' height='100' fill='url(#g)'/><text x='50' y='60' font-family='Georgia,serif' font-size='30' text-anchor='middle' fill='rgba(255,255,255,.85)'>${esc(
    symbol.slice(0, 3)
  )}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
